import "@fontsource/ubuntu";
import {
  ChevronLeft,
  History,
  Map,
  Menu,
  TravelExplore,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  darken,
  Divider,
  Drawer,
  IconButton,
  lighten,
  ListItemIcon,
  ListItemText,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSpecialRights } from "../../../context/SpecialRightsContext";
import { iresoftColors } from "../../../theme/ireColors";
import { tokens } from "../../../theme/theme";
import "./Topbar.css";

const Topbar = () => {
  const { hasSpecialRights } = useSpecialRights();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const isSmallScreen = useMediaQuery("(max-width:1040px)");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  const isActiveRoute = (route: string) => location.pathname === route;

  const navigateToWelcome = () => {
    navigate("/");
  };

  const menuItems = [
    { href: "/", label: t("menu.welcome"), icon: <Map /> },
    {
      href: "/HistoryReading",
      label: t("menu.historyReading"),
      icon: <History />,
    },
    ...(hasSpecialRights
      ? [
          {
            href: "/CurrentlyReading",
            label: t("menu.currentlyReading"),
            icon: <TravelExplore />,
          },
        ]
      : []),
  ];

  const shadowColor =
    theme.palette.mode === "dark"
      ? alpha(colors.primary[200], 0.175)
      : alpha(colors.primary[300], 0.175);

  return (
    <Box
      className="topbar"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${
          theme.palette.mode === "dark"
            ? alpha(colors.primary[100], 0.5)
            : alpha(colors.primary[300], 0.5)
        }`,
        backgroundColor: theme.palette.background.default,
        boxShadow: `0 0.225rem 0.25rem ${shadowColor}`,
        padding: "0 16px",
        maxHeight: 64,
      }}
    >
      {isSmallScreen && (
        <IconButton onClick={handleDrawerOpen} aria-label="menu">
          <Menu />
        </IconButton>
      )}

      {/* Left: Logo and Title */}
      <Box
        display="flex"
        alignItems="center"
        sx={{ cursor: "pointer" }}
        onClick={navigateToWelcome}
      >
        <img
          src="/imgs/logo_with_text.png"
          alt="Logo"
          style={{
            height: "55px",
            marginRight: "0.8rem",
          }}
        />
        {/*         {!isSmallScreen && (
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Pirulen",
              fontWeight: 400,
              letterSpacing: "0.05rem",
            }}
          >
            IRESOFT
          </Typography>
        )} */}
      </Box>

      {/* Center: Navigation Menu */}
      {isSmallScreen ? (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerClose}
          disableScrollLock
        >
          <Box
            sx={{
              width: 260,
              display: "flex",
              flexDirection: "column",
              padding: 2,
              position: "relative",
            }}
            onClick={handleDrawerClose}
            onKeyDown={handleDrawerClose}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.href}
                onClick={() => navigate(item.href)}
                selected={isActiveRoute(item.href)}
                sx={{
                  "&:hover": {
                    backgroundColor: lighten(iresoftColors.iresoftRed, 0.4),
                  },
                  "&.Mui-selected": {
                    backgroundColor: iresoftColors.iresoftRed,
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: darken(iresoftColors.iresoftDarkRed, 0),
                    },
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText>{item.label}</ListItemText>
              </MenuItem>
            ))}

            <Divider sx={{ my: 1 }} />

            <IconButton
              onClick={handleDrawerClose}
              sx={{
                position: "absolute",
                right: 5,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: 6,
                zIndex: 1,
                "&:hover": {
                  backgroundColor: darken(theme.palette.background.paper, 0.1),
                },
              }}
              aria-label={t("menu.tooltip.close")}
            >
              <ChevronLeft />
            </IconButton>
          </Box>
        </Drawer>
      ) : (
        <Box sx={{ display: "flex", gap: "1.5rem", ml: -30 }}>
          {menuItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              style={{
                color: isActiveRoute(item.href)
                  ? iresoftColors.iresoftRed
                  : iresoftColors.black,
                fontWeight: isActiveRoute(item.href) ? 900 : 400,
                textDecoration: "none",
                fontSize: "1rem",
                padding: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {item.label}
            </Link>
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}></Box>
    </Box>
  );
};

export default React.memo(Topbar);
